import { useMutation } from '@apollo/client';
import type { DealProviderConfig } from '@openx/components-deal/lib/utils/context/DealContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CreateDealTroubleshootingFactJobDocument } from '../../../graphql/mutation/deals/CreateDealTroubleshootingFactJob.generated';
import { CreateDealTroubleshootingNrtJobDocument } from '../../../graphql/mutation/deals/CreateDealTroubleshootingNrtJob.generated';
import { useDealTroubleshootingJobFetch } from './troubleshootingFunnel/useDealTroubleshootingJobFetch';

export const useTroubleshootingFetch: DealProviderConfig['useTroubleshootingFetch'] = (dealUid, params) => {
  const [jobId, setJobId] = useState<string | undefined>();
  const requestIdRef = useRef(0);
  const [createNrtJob] = useMutation(CreateDealTroubleshootingNrtJobDocument);
  const [createFactJob] = useMutation(CreateDealTroubleshootingFactJobDocument);

  const {
    data: funnelData,
    error: funnelError,
    isLoading: isFunnelLoading,
  } = useDealTroubleshootingJobFetch({
    job_id: jobId ?? '',
    deal_id: dealUid,
    enabled: !!jobId,
  });

  const createJobAsync = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!params) return;

    try {
      // For 24h period use NRT job with lookback_hours
      if (params.lookbackHours) {
        const { data } = await createNrtJob({
          variables: {
            deal_id: dealUid,
            lookback_hours: params.lookbackHours,
            dsp_id: params.dspId,
          },
        });
        if (requestId === requestIdRef.current && data?.createDealTroubleshootingNRTJob.job_id) {
          setJobId(data.createDealTroubleshootingNRTJob.job_id);
        }
      } else if (params.startDate && params.endDate) {
        const { data } = await createFactJob({
          variables: {
            deal_id: dealUid,
            start_date: params.startDate,
            end_date: params.endDate,
            resolution: params.resolution,
            dsp_id: params.dspId,
          },
        });
        if (requestId === requestIdRef.current && data?.createDealTroubleshootingFactJob.job_id) {
          setJobId(data.createDealTroubleshootingFactJob.job_id);
        }
      }
    } catch {
      // Job creation failed, handled by mutation hook
    }
  }, [params, dealUid, createNrtJob, createFactJob]);

  useEffect(() => {
    setJobId(undefined);
    createJobAsync();
  }, [createJobAsync]);

  // Map GraphQL data to chart format - filter out null values
  // Handle both GraphQL (dealTroubleshootingJob with output) and REST formats
  // biome-ignore lint/suspicious/noExplicitAny: funnelData is union type from GraphQL/REST
  const graphqlData = funnelData as any;

  // Extract output if it's GraphQL format (dealTroubleshootingJob), otherwise use data directly
  const funnelApiData = graphqlData?.output ?? graphqlData;

  const troubleshoot = (funnelApiData?.time_series_stats ?? [])
    // biome-ignore lint/suspicious/noExplicitAny: item type depends on GraphQL schema
    .filter((item: any): item is NonNullable<typeof item> => item !== null);

  // Normalize error to string | null for TroubleshootingType
  const normalizedError = funnelError
    ? typeof funnelError === 'object' && funnelError !== null && 'message' in funnelError
      ? String((funnelError as { message?: string }).message)
      : String(funnelError)
    : null;

  const refetch = useCallback(async () => {
    setJobId(undefined);
    await createJobAsync();
  }, [createJobAsync]);

  return {
    isLoading: !jobId || isFunnelLoading,
    troubleshoot,
    funnelData: funnelApiData,
    error: normalizedError,
    refetch,
  };
};
